import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, BookOpen, Target, FileText, Calendar, ArrowLeft, CheckCircle } from 'lucide-react';
import { mockChildren, mockSubjects, mockGoals, mockCompletedWork, mockLessonPlans, mockPendingWork, mockUser } from '../mock';
import { useToast } from '../hooks/use-toast';

const ChildrenPage = () => {
  const [children, setChildren] = useState(mockChildren);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedWork, setSelectedWork] = useState(null);
  const [isGrading, setIsGrading] = useState(false);
  const [gradeData, setGradeData] = useState({ percentage: '', notes: '' });
  const { toast } = useToast();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    grade: '',
    age: ''
  });

  const handleAddChild = () => {
    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'];
    const newChild = {
      id: String(children.length + 1),
      ...formData,
      age: parseInt(formData.age),
      profileColor: colors[children.length % colors.length],
      subjects: []
    };

    setChildren([...children, newChild]);
    toast({
      title: 'Child Added!',
      description: `${newChild.name} has been added successfully.`
    });

    setFormData({ name: '', grade: '', age: '' });
    setIsDialogOpen(false);
  };

  const handleGradeWork = (work) => {
    setSelectedWork(work);
    setGradeData({ percentage: '', notes: '' });
    setIsGrading(true);
  };

  const handleSubmitGrade = () => {
    const percentage = parseFloat(gradeData.percentage);
    let letterGrade = '';
    
    if (mockUser.grading_enabled && percentage) {
      for (const [grade, range] of Object.entries(mockUser.grading_scale)) {
        if (percentage >= range.min && percentage <= range.max) {
          letterGrade = grade;
          break;
        }
      }
    }

    toast({
      title: 'Grade Submitted!',
      description: `${selectedWork.title} has been graded: ${letterGrade || gradeData.percentage + '%'}`
    });

    setIsGrading(false);
    setSelectedWork(null);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-teal-50">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 mb-2">My Children</h1>
            <p className="text-slate-600 text-lg">Manage your homeschool students</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-gradient-to-r from-blue-600 to-teal-600 hover:from-blue-700 hover:to-teal-700 text-white px-6 py-6">
                <Plus className="w-5 h-5 mr-2" />
                Add Child
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold text-slate-900">Add New Child</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <Label htmlFor="name">Child's Name</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Emma Johnson"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="grade">Grade Level</Label>
                  <Input
                    id="grade"
                    value={formData.grade}
                    onChange={(e) => setFormData({ ...formData, grade: e.target.value })}
                    placeholder="5th Grade"
                    className="mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="age">Age</Label>
                  <Input
                    id="age"
                    type="number"
                    value={formData.age}
                    onChange={(e) => setFormData({ ...formData, age: e.target.value })}
                    placeholder="10"
                    className="mt-2"
                  />
                </div>
                <Button
                  onClick={handleAddChild}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6"
                  disabled={!formData.name || !formData.grade || !formData.age}
                >
                  Add Child
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {/* Show either grid or detailed view */}
        {!selectedChild ? (
          /* Children Grid */
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {children.map(child => (
              <Card 
                key={child.id} 
                className="overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 bg-white cursor-pointer group"
                onClick={() => setSelectedChild(child)}
              >
              <div
                className="h-24 bg-gradient-to-br from-blue-400 to-teal-400 transition-all duration-300 group-hover:h-28"
                style={{ background: `linear-gradient(135deg, ${child.profileColor}, ${child.profileColor}dd)` }}
              />
              <div className="p-6 -mt-12">
                <div
                  className="w-20 h-20 rounded-full bg-white border-4 border-white shadow-lg flex items-center justify-center text-3xl font-bold mb-4 transition-transform duration-300 group-hover:scale-110"
                  style={{ color: child.profileColor }}
                >
                  {child.name.charAt(0)}
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">{child.name}</h3>
                <p className="text-slate-600 mb-4">{child.grade} • {child.age} years old</p>

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <BookOpen className="w-4 h-4" />
                      <span>Subjects</span>
                    </div>
                    <span className="font-semibold text-slate-900">{child.subjects.length}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <Target className="w-4 h-4" />
                      <span>Active Goals</span>
                    </div>
                    <span className="font-semibold text-slate-900">2</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-2 text-slate-600">
                      <FileText className="w-4 h-4" />
                      <span>Completed Work</span>
                    </div>
                    <span className="font-semibold text-slate-900">8</span>
                  </div>
                </div>

                <div className="flex items-center justify-center gap-2 text-slate-500 text-sm group-hover:text-blue-600 transition-colors">
                  <span>Click to view details</span>
                  <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </div>
              </div>
            </Card>
          ))}

            {children.length === 0 && (
              <div className="col-span-full text-center py-20">
                <p className="text-2xl text-slate-500 mb-4">No children added yet</p>
                <p className="text-slate-400">Click "Add Child" to get started!</p>
              </div>
            )}
          </div>
        ) : (
          /* Child Details View */
          <div className="space-y-6">
            {/* Back Button & Header */}
            <Button
              variant="outline"
              onClick={() => setSelectedChild(null)}
              className="mb-4"
            >
              ← Back to All Children
            </Button>

            {/* Child Info Header */}
            <Card className="p-6 border-0 shadow-lg bg-gradient-to-br from-blue-50 to-teal-50">
              <div className="flex items-center gap-4">
                <div
                  className="w-20 h-20 rounded-full bg-white border-4 border-white shadow-lg flex items-center justify-center text-4xl font-bold"
                  style={{ color: selectedChild.profileColor }}
                >
                  {selectedChild.name.charAt(0)}
                </div>
                <div>
                  <h2 className="text-3xl font-bold text-slate-900">{selectedChild.name}</h2>
                  <p className="text-slate-600 text-lg">{selectedChild.grade} • {selectedChild.age} years old</p>
                </div>
              </div>
            </Card>

            {/* Stats Grid - Now Clickable */}
            <div className="grid md:grid-cols-4 gap-6">
              <Card 
                className="p-6 border-0 shadow-lg bg-white hover:shadow-xl transition-all cursor-pointer transform hover:-translate-y-1 group"
                onClick={() => document.getElementById('subjects-section')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <BookOpen className="w-10 h-10 text-blue-600 mb-3 group-hover:scale-110 transition-transform" />
                <p className="text-3xl font-bold text-slate-900 mb-1">{selectedChild.subjects.length}</p>
                <p className="text-slate-600 group-hover:text-blue-600 transition-colors">Subjects</p>
              </Card>
              <Card 
                className="p-6 border-0 shadow-lg bg-white hover:shadow-xl transition-all cursor-pointer transform hover:-translate-y-1 group"
                onClick={() => document.getElementById('goals-section')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Target className="w-10 h-10 text-teal-600 mb-3 group-hover:scale-110 transition-transform" />
                <p className="text-3xl font-bold text-slate-900 mb-1">
                  {mockGoals.filter(g => g.childId === selectedChild.id).length}
                </p>
                <p className="text-slate-600 group-hover:text-teal-600 transition-colors">Active Goals</p>
              </Card>
              <Card 
                className="p-6 border-0 shadow-lg bg-white hover:shadow-xl transition-all cursor-pointer transform hover:-translate-y-1 group"
                onClick={() => document.getElementById('work-section')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <FileText className="w-10 h-10 text-purple-600 mb-3 group-hover:scale-110 transition-transform" />
                <p className="text-3xl font-bold text-slate-900 mb-1">
                  {mockCompletedWork.filter(w => w.childId === selectedChild.id).length}
                </p>
                <p className="text-slate-600 group-hover:text-purple-600 transition-colors">Completed Work</p>
              </Card>
              <Card 
                className="p-6 border-0 shadow-lg bg-white hover:shadow-xl transition-all cursor-pointer transform hover:-translate-y-1 group"
                onClick={() => document.getElementById('plans-section')?.scrollIntoView({ behavior: 'smooth' })}
              >
                <Calendar className="w-10 h-10 text-amber-600 mb-3 group-hover:scale-110 transition-transform" />
                <p className="text-3xl font-bold text-slate-900 mb-1">
                  {mockLessonPlans.filter(p => p.childId === selectedChild.id).length}
                </p>
                <p className="text-slate-600 group-hover:text-amber-600 transition-colors">Lesson Plans</p>
              </Card>
            </div>

            {/* Subjects Section */}
            <Card id="subjects-section" className="p-6 border-0 shadow-lg bg-white scroll-mt-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <BookOpen className="w-6 h-6 text-blue-600" />
                Subjects
              </h3>
              <div className="flex flex-wrap gap-3">
                {selectedChild.subjects.map((subjectName, idx) => {
                  const subject = mockSubjects.find(s => s.name === subjectName);
                  const subjectWorkCount = mockCompletedWork.filter(
                    w => w.childId === selectedChild.id && mockSubjects.find(s => s.id === w.subjectId)?.name === subjectName
                  ).length;
                  const pendingCount = mockPendingWork.filter(
                    w => w.child_id === selectedChild.id && mockSubjects.find(s => s.id === w.subject_id)?.name === subjectName
                  ).length;
                  
                  return (
                    <div
                      key={idx}
                      className="px-5 py-3 rounded-lg flex items-center gap-3 shadow-sm hover:shadow-md transition-all cursor-pointer transform hover:scale-105 relative"
                      style={{ backgroundColor: `${subject?.color}20`, color: subject?.color }}
                      onClick={() => setSelectedSubject(subject)}
                    >
                      <span className="text-2xl">{subject?.icon}</span>
                      <div>
                        <span className="font-semibold text-lg block">{subjectName}</span>
                        <span className="text-xs opacity-75">{subjectWorkCount} completed</span>
                      </div>
                      {pendingCount > 0 && (
                        <Badge className="bg-red-500 text-white ml-2">{pendingCount} pending</Badge>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Goals Section */}
            <Card id="goals-section" className="p-6 border-0 shadow-lg bg-white scroll-mt-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Target className="w-6 h-6 text-teal-600" />
                Active Goals
              </h3>
              <div className="space-y-4">
                {mockGoals.filter(g => g.childId === selectedChild.id).map(goal => (
                  <div key={goal.id} className="p-4 bg-gradient-to-r from-teal-50 to-green-50 rounded-lg border border-teal-200">
                    <h4 className="font-bold text-slate-900 mb-2">{goal.title}</h4>
                    <p className="text-sm text-slate-600 mb-3">{goal.description}</p>
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-slate-600">Progress</span>
                        <span className="font-semibold text-teal-700">{goal.progress}%</span>
                      </div>
                      <Progress value={goal.progress} className="h-3" />
                    </div>
                  </div>
                ))}
                {mockGoals.filter(g => g.childId === selectedChild.id).length === 0 && (
                  <p className="text-slate-500 text-center py-8">No active goals yet</p>
                )}
              </div>
            </Card>

            {/* Recent Work Section */}
            <Card id="work-section" className="p-6 border-0 shadow-lg bg-white scroll-mt-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <FileText className="w-6 h-6 text-purple-600" />
                Recent Completed Work
              </h3>
              <div className="space-y-3">
                {mockCompletedWork.filter(w => w.childId === selectedChild.id).slice(0, 5).map(work => {
                  const subject = mockSubjects.find(s => s.id === work.subjectId);
                  return (
                    <div 
                      key={work.id} 
                      className="flex items-center justify-between p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer hover:shadow-md"
                      onClick={() => handleGradeWork(work)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-2xl">{subject?.icon}</div>
                        <div>
                          <p className="font-semibold text-slate-900">{work.title}</p>
                          <p className="text-xs text-slate-500">{new Date(work.completedDate).toLocaleDateString()}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                          {work.grade}
                        </div>
                        <span className="text-sm text-slate-400">Click to review</span>
                      </div>
                    </div>
                  );
                })}
                {mockCompletedWork.filter(w => w.childId === selectedChild.id).length === 0 && (
                  <p className="text-slate-500 text-center py-8">No completed work yet</p>
                )}
              </div>
            </Card>

            {/* Lesson Plans Section */}
            <Card id="plans-section" className="p-6 border-0 shadow-lg bg-white scroll-mt-6">
              <h3 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2">
                <Calendar className="w-6 h-6 text-amber-600" />
                Lesson Plans
              </h3>
              <div className="grid md:grid-cols-2 gap-4">
                {mockLessonPlans.filter(p => p.childId === selectedChild.id).map(plan => {
                  const subject = mockSubjects.find(s => s.id === plan.subjectId);
                  return (
                    <div key={plan.id} className="p-4 bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg border border-amber-200">
                      <div className="flex items-start gap-3 mb-3">
                        <div
                          className="w-10 h-10 rounded-lg flex items-center justify-center text-xl"
                          style={{ backgroundColor: `${subject?.color}20`, color: subject?.color }}
                        >
                          {subject?.icon}
                        </div>
                        <div className="flex-1">
                          <h4 className="font-bold text-slate-900 mb-1">{plan.title}</h4>
                          <div
                            className="text-sm text-slate-600 prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: plan.description || '' }}
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs text-slate-500">
                          <span>Start: {new Date(plan.startDate).toLocaleDateString()}</span>
                          <span>End: {new Date(plan.endDate).toLocaleDateString()}</span>
                        </div>
                        <div className="px-3 py-1 bg-amber-200 text-amber-800 rounded-full text-xs font-semibold inline-block">
                          In Progress
                        </div>
                      </div>
                    </div>
                  );
                })}
                {mockLessonPlans.filter(p => p.childId === selectedChild.id).length === 0 && (
                  <p className="text-slate-500 text-center py-8 col-span-2">No lesson plans yet</p>
                )}
              </div>
            </Card>
          </div>
        )}

        {/* Subject Detail Dialog */}
        <Dialog open={!!selectedSubject} onOpenChange={() => setSelectedSubject(null)}>
          <DialogContent className="bg-white max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                {selectedSubject && (
                  <>
                    <span className="text-3xl">{selectedSubject.icon}</span>
                    {selectedSubject.name} - {selectedChild?.name}
                  </>
                )}
              </DialogTitle>
            </DialogHeader>
            {selectedSubject && selectedChild && (
              <div className="space-y-6 mt-4">
                {/* Pending Work for this Subject */}
                {mockPendingWork.filter(
                  w => w.child_id === selectedChild.id && w.subject_id === selectedSubject.id
                ).length > 0 && (
                  <div>
                    <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                      <Badge className="bg-amber-500">Pending Review</Badge>
                    </h3>
                    <div className="space-y-3">
                      {mockPendingWork
                        .filter(w => w.child_id === selectedChild.id && w.subject_id === selectedSubject.id)
                        .map(work => (
                          <Card key={work.id} className="p-4 bg-amber-50 border-amber-200">
                            <div className="flex items-start justify-between">
                              <div>
                                <h4 className="font-semibold text-slate-900 mb-1">{work.title}</h4>
                                <p className="text-sm text-slate-600 mb-2">{work.description}</p>
                                <p className="text-xs text-slate-500">
                                  Submitted: {new Date(work.submitted_date).toLocaleDateString()}
                                </p>
                              </div>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 text-white"
                                onClick={() => {
                                  setSelectedSubject(null);
                                  navigate('/pending');
                                }}
                              >
                                Grade Now
                              </Button>
                            </div>
                          </Card>
                        ))}
                    </div>
                  </div>
                )}

                {/* Completed Work for this Subject */}
                <div>
                  <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Completed Work
                  </h3>
                  <div className="space-y-3">
                    {mockCompletedWork
                      .filter(w => w.childId === selectedChild.id && w.subjectId === selectedSubject.id)
                      .map(work => (
                        <Card 
                          key={work.id} 
                          className="p-4 hover:bg-slate-50 transition-colors cursor-pointer"
                          onClick={() => {
                            setSelectedSubject(null);
                            handleGradeWork(work);
                          }}
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className="font-semibold text-slate-900 mb-1">{work.title}</h4>
                              <p className="text-sm text-slate-600 mb-2">{work.description}</p>
                              <p className="text-xs text-slate-500">
                                Completed: {new Date(work.completedDate).toLocaleDateString()}
                              </p>
                            </div>
                            <div className="px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                              {work.grade}
                            </div>
                          </div>
                        </Card>
                      ))}
                    {mockCompletedWork.filter(
                      w => w.childId === selectedChild.id && w.subjectId === selectedSubject.id
                    ).length === 0 && (
                      <p className="text-slate-500 text-center py-8">No completed work for this subject yet</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Grading Dialog */}
        <Dialog open={isGrading} onOpenChange={setIsGrading}>
          <DialogContent className="bg-white max-w-2xl">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold text-slate-900">
                Review Work: {selectedWork?.title}
              </DialogTitle>
            </DialogHeader>
            {selectedWork && (
              <div className="space-y-4 mt-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-semibold text-slate-900 mb-2">Current Grade</h4>
                  <div className="flex items-center gap-4">
                    <span className="text-3xl font-bold text-green-600">{selectedWork.grade}</span>
                    <div className="flex-1">
                      <p className="text-sm text-slate-600">{selectedWork.description}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        Completed: {new Date(selectedWork.completedDate).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                </div>

                {selectedWork.notes && (
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <h4 className="font-semibold text-blue-900 mb-2">Previous Feedback</h4>
                    <p className="text-sm text-blue-800">{selectedWork.notes}</p>
                  </div>
                )}

                <div>
                  <Label htmlFor="new-percentage">Update Grade (Percentage)</Label>
                  <Input
                    id="new-percentage"
                    type="number"
                    min="0"
                    max="100"
                    value={gradeData.percentage}
                    onChange={(e) => setGradeData({ ...gradeData, percentage: e.target.value })}
                    placeholder="Enter new percentage"
                    className="mt-2"
                  />
                  {gradeData.percentage && (
                    <p className="text-sm text-slate-600 mt-1">
                      Letter Grade: {
                        Object.entries(mockUser.grading_scale).find(([grade, range]) =>
                          parseFloat(gradeData.percentage) >= range.min &&
                          parseFloat(gradeData.percentage) <= range.max
                        )?.[0] || 'N/A'
                      }
                    </p>
                  )}
                </div>

                <div>
                  <Label htmlFor="new-notes">Update Feedback</Label>
                  <Textarea
                    id="new-notes"
                    value={gradeData.notes}
                    onChange={(e) => setGradeData({ ...gradeData, notes: e.target.value })}
                    placeholder="Add or update feedback..."
                    rows={3}
                    className="mt-2"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    onClick={handleSubmitGrade}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white py-6"
                    disabled={!gradeData.percentage}
                  >
                    Update Grade
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setIsGrading(false)}
                    className="px-6"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default ChildrenPage;